/**
 * PKCE (Proof Key for Code Exchange) Helpers
 *
 * Implements the PKCE extension for OAuth 2.0 authorization.
 * Required for Spotify Web API authorization in SPAs.
 *
 * @see https://developer.spotify.com/documentation/web-api/tutorials/code-pkce-flow
 */

/**
 * Generate a cryptographically random code verifier
 *
 * The code verifier is a high-entropy cryptographic random string
 * with a length between 43 and 128 characters.
 *
 * @returns {string} A random 128-character hex string
 */
export function generateCodeVerifier() {
  const array = new Uint8Array(64);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Generate a code challenge from a code verifier
 *
 * The code challenge is the Base64-URL-encoded SHA256 hash
 * of the code verifier.
 *
 * @param {string} verifier - The code verifier string
 * @returns {Promise<string>} The Base64-URL-encoded challenge
 */
export async function generateCodeChallenge(verifier) {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(digest);
}

/**
 * Encode an ArrayBuffer to Base64-URL format
 *
 * Base64-URL is Base64 with:
 * - '+' replaced with '-'
 * - '/' replaced with '_'
 * - Trailing '=' padding removed
 *
 * @param {ArrayBuffer} buffer - The buffer to encode
 * @returns {string} Base64-URL encoded string
 */
export function base64UrlEncode(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  const base64 = btoa(binary);
  return base64
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/**
 * Store PKCE verifier in session storage
 *
 * @param {string} verifier - The code verifier to store
 */
export function storePKCEVerifier(verifier) {
  sessionStorage.setItem('spotify_pkce_verifier', verifier);
}

/**
 * Retrieve and clear PKCE verifier from session storage
 *
 * @returns {string|null} The stored verifier, or null if not found
 */
export function retrievePKCEVerifier() {
  const verifier = sessionStorage.getItem('spotify_pkce_verifier');
  sessionStorage.removeItem('spotify_pkce_verifier');
  return verifier;
}

/**
 * Generate a complete PKCE pair (verifier + challenge)
 *
 * Convenience function that generates both values at once.
 *
 * @returns {Promise<{verifier: string, challenge: string}>}
 */
export async function generatePKCEPair() {
  const verifier = generateCodeVerifier();
  const challenge = await generateCodeChallenge(verifier);
  return { verifier, challenge };
}
